import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';

import {
  SuburbAreaEditResult,
  SuburbAreasService,
} from '../../../../services/suburb-areas.service';
import { MapEdit, MapRow } from '../map/suburb-map.engine';
import { WorkspaceStateService } from './workspace-state.service';

/** Map edits were saved (or undone): the suburbs they touched, or null when any may have changed. */
export interface AreasSaved {
  codes: string[] | null;
}

/**
 * Saving the map's edits (#256, #260): one action = one PATCH /website/suburb-areas = one server batch = one undo
 * step, optimistic (the engine has already applied it) and queued in order, so each edit's `from` is what the previous
 * save left. A save that fails, or a loss the user cancels, puts the touched suburbs back exactly as they were. Undo
 * undoes the latest batch on the server.
 *
 * It also keeps which sub-regions' members changed here since their page was last published ("publish to show the
 * change"), and announces each save so the open panel can reload (`saved$`).
 */
@Injectable()
export class MapEditsService implements OnDestroy {
  readonly saved$ = new Subject<AreasSaved>();
  undoing = false;

  private queue: Promise<void> = Promise.resolve();
  /** Sub-regions whose members changed here since their page was last published. */
  private stalePages = new Set<string>();

  constructor(
    private api: SuburbAreasService,
    private state: WorkspaceStateService
  ) {}

  ngOnDestroy(): void {
    this.saved$.complete();
  }

  /** An action from the map (the engine's onAction): saved in turn. */
  save(edits: MapEdit[], label: string, before: MapRow[]): void {
    this.enqueue(
      () => this.send(edits, label, before),
      () => this.saved$.next({ codes: edits.map((e) => e.salCode) })
    );
  }

  /**
   * Run `work` after every save before it (it may write suburb areas too, like Fill from council areas); `settled`
   * runs when it is done either way. The status line says "Saving…" meanwhile.
   */
  enqueue(work: () => Promise<void>, settled: () => void): void {
    this.state.saving++;
    this.queue = this.queue.then(work).finally(() => {
      this.state.saving--;
      settled();
      this.state.redraw();
    });
  }

  /** A batch the server can undo, for Cmd+Z. */
  pushUndo(batchId: string, label: string): void {
    this.state.undoStack.push({ batchId, label });
  }

  markStale(subRegionId: string): void {
    this.stalePages.add(subRegionId);
  }

  /** Its page was just published: it shows the members as they are. */
  clearStale(subRegionId: string): void {
    this.stalePages.delete(subRegionId);
  }

  isStale(subRegionId: string): boolean {
    return this.stalePages.has(subRegionId);
  }

  /**
   * Undo the latest saved map action on the server. The step leaves the stack only once the server has undone it,
   * so a failed undo can be retried, and a second Cmd+Z while one is in flight is ignored.
   */
  async undo(): Promise<void> {
    if (this.state.readOnly || this.undoing) return;
    this.undoing = true;
    try {
      await this.queue;
      const stack = this.state.undoStack;
      const last = stack[stack.length - 1];
      if (!last) return this.state.setStatus('Nothing to undo.');
      try {
        const res = await this.api.undo(last.batchId).toPromise();
        if (res.rows) this.state.engine.applyRows(res.rows);
        else await this.resync();
        stack.pop();
        const conflicts = res.conflicts ? `, except ${res.conflicts} suburb(s) changed since` : '';
        this.state.setStatus(`Undid ${last.label}${conflicts}.`, !!res.conflicts);
        this.saved$.next({ codes: null });
      } catch (err: any) {
        if (err?.status === 409 && /already/i.test(serverError(err)?.title || '')) stack.pop();
        this.state.setStatus(
          `Couldn't undo ${last.label}: ${saveErrorText(err, err?.message)}`,
          true
        );
      }
    } finally {
      this.undoing = false;
      this.state.redraw();
    }
  }

  private async send(
    edits: MapEdit[],
    label: string,
    before: MapRow[],
    confirmLoss = false
  ): Promise<void> {
    try {
      const res = await this.api.edit(edits, confirmLoss).toPromise();
      this.state.engine.applyRows(res.rows);
      this.afterSave(res, edits, label, before);
    } catch (err: any) {
      const e = serverError(err);
      if (err?.status === 409 && !confirmLoss && /confirm/i.test(e?.title || '')) {
        if (
          await this.state.ask(
            (e?.html || e?.message || []).join(' ') || 'This removes suburbs. Go ahead?'
          )
        )
          return this.send(edits, label, before, true);
        this.state.engine.applyRows(before);
        return this.state.setStatus(`Cancelled ${label}: nothing was saved.`);
      }
      this.state.engine.applyRows(before);
      this.state.setStatus(
        `Couldn't save ${label}: ${saveErrorText(err, err?.message || 'unknown error')}`,
        true
      );
      await this.resync();
    }
  }

  private afterSave(
    res: SuburbAreaEditResult,
    edits: MapEdit[],
    label: string,
    before: MapRow[]
  ): void {
    const s = res.summary;
    const changed =
      s.assigned +
      s.reassigned +
      s.unassigned +
      s.covered +
      s.uncovered +
      (s.joined ?? 0) +
      (s.left ?? 0);
    for (const e of edits) if (e.subRegionId) this.markStale(e.subRegionId);
    // A move or unassign drops memberships on the server: those sub-regions changed too.
    if (s.left) for (const b of before) for (const id of b.subRegions ?? []) this.markStale(id);
    if (res.batchId && changed) this.pushUndo(res.batchId, label);
    const notes: string[] = [];
    if (res.refused.length) {
      const named = res.refused
        .slice(0, 3)
        .map((r) => r.page || r.reason)
        .join(', ');
      notes.push(`${res.refused.length} left alone: ${named}${res.refused.length > 3 ? '…' : ''}`);
    }
    if (res.conflicts.length)
      notes.push(
        `${res.conflicts.length} had been changed by someone else; the map now shows what's saved`
      );
    if (notes.length) this.state.setStatus(`Saved ${label}. ${notes.join('. ')}.`, true);
    else
      this.state.setStatus(
        `Saved ${label} (${changed.toLocaleString()} change${changed === 1 ? '' : 's'}).`
      );
  }

  /** Reload every stored row, after a failure left the map unsure of what's saved. */
  private async resync(): Promise<void> {
    try {
      const rows = await this.api.allRows();
      const named = new Set(rows.map((r) => r.salCode));
      const gone = [...this.state.engine.areas.keys()]
        .filter((k) => !named.has(k))
        .map((salCode) => ({ salCode, regionId: null, covered: false }));
      this.state.engine.applyRows([...rows, ...gone]);
    } catch {
      this.state.setStatus('The map may be out of date: close and reopen Suburbs.', true);
    }
  }
}

/** The server's error envelope, when the failure was an HTTP response. */
function serverError(err: unknown): any {
  return err instanceof HttpErrorResponse ? err.error?.error : null;
}

/** A save's or an undo's failure as one line: the server's HTML or message, else the thrown error's message. */
function saveErrorText(err: unknown, thrown: string): string {
  const e = serverError(err);
  return (e?.html || e?.message || [thrown]).join(' ');
}
